import { toast } from "sonner";
import {
  useCreatePlatformWeighing,
  useDeletePlatformWeighing,
  useSignOffPlatformWeighing,
  useUnlockPlatformWeighing,
  useUpdatePlatformWeighing,
} from "@/api/hooks/use-platform-weighings";
import type { BoatId, PlatformWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type PlatformWeighing = components["schemas"]["PlatformWeighingResponse-Output"];
type PlatformWeighingCreate = components["schemas"]["PlatformWeighingCreate"];
type PlatformWeighingUpdate = components["schemas"]["PlatformWeighingUpdate"];

export interface PlatformWeighingActions {
  /** Resolves with the created weighing; rejects on failure so callers can keep their UI open for retry. */
  handleCreate: (data: PlatformWeighingCreate) => Promise<PlatformWeighing>;
  /** Resolves on success; rejects on failure so callers can keep their UI open for retry. */
  handleUpdate: (
    id: PlatformWeighingId,
    data: PlatformWeighingUpdate,
  ) => Promise<void>;
  handleSignOff: (id: PlatformWeighingId) => void;
  /** Awaitable variant for save-then-sign-off chains; rejects on failure.
   * `overrideGate` signs off past a failed repeatability gate (recorded). */
  handleSignOffAsync: (id: PlatformWeighingId, overrideGate?: boolean) => Promise<void>;
  handleUnlock: (id: PlatformWeighingId) => void;
  handleDelete: (id: PlatformWeighingId) => void;
  isCreating: boolean;
  isUpdating: boolean;
  isSigningOff: boolean;
  isUnlocking: boolean;
  isDeleting: boolean;
  weighings: PlatformWeighing[];
  signedOffCount: number;
  activeWeighing: PlatformWeighing | undefined;
}

export function usePlatformWeighingActions(
  boatId: BoatId,
  weighings: PlatformWeighing[] | undefined,
): PlatformWeighingActions {
  const create = useCreatePlatformWeighing(boatId);
  const update = useUpdatePlatformWeighing(boatId);
  const signOff = useSignOffPlatformWeighing(boatId);
  const unlock = useUnlockPlatformWeighing(boatId);
  const del = useDeletePlatformWeighing(boatId);

  const list = weighings ?? [];
  const signedOff = list.filter((w) => w.state === "signed_off");
  const activeWeighing = signedOff[0]; // newest by measured_at desc

  const handleCreate = (data: PlatformWeighingCreate) =>
    create.mutateAsync(data).then(
      (created) => {
        toast.success("Platform weighing created");
        return created;
      },
      (err: Error) => {
        toast.error(err.message);
        throw err;
      },
    );

  const handleUpdate = (id: PlatformWeighingId, data: PlatformWeighingUpdate) =>
    update.mutateAsync({ weighingId: id, data }).then(
      () => {
        toast.success("Platform weighing updated");
      },
      (err: Error) => {
        toast.error(err.message);
        throw err;
      },
    );

  const handleSignOffAsync = (id: PlatformWeighingId, overrideGate = false) =>
    signOff.mutateAsync({ weighingId: id, overrideGate }).then(
      () => {
        toast.success("Platform weighing signed off");
      },
      (err: Error) => {
        toast.error(err.message);
        throw err;
      },
    );

  const handleSignOff = (id: PlatformWeighingId) => {
    void handleSignOffAsync(id).catch(() => {});
  };

  const handleUnlock = (id: PlatformWeighingId) => {
    unlock.mutate(id, {
      onSuccess: () => toast.success("Platform weighing unlocked"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleDelete = (id: PlatformWeighingId) => {
    del.mutate(id, {
      onSuccess: () => toast.success("Platform weighing deleted"),
      onError: (err) => toast.error(err.message),
    });
  };

  return {
    handleCreate,
    handleUpdate,
    handleSignOff,
    handleSignOffAsync,
    handleUnlock,
    handleDelete,
    isCreating: create.isPending,
    isUpdating: update.isPending,
    isSigningOff: signOff.isPending,
    isUnlocking: unlock.isPending,
    isDeleting: del.isPending,
    weighings: list,
    signedOffCount: signedOff.length,
    activeWeighing,
  };
}
